import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";

import MapFilterDropdown, {
  MapFilterDropdownOption,
} from "@/components/ui/MapFilterDropdown.web";
import { NOTIFICATION_TYPES } from "@/constants/notification-types";
import { useThemeColor } from "@/hooks/useThemeColor";
import {
  MapPostsFilters,
  RECENCY_LABEL_KEY,
  RECENCY_PRESETS,
  RecencyPreset,
} from "@/types/mapPostsFilters";

type Props = {
  filters: MapPostsFilters;
  cities: string[];
  eventTypes: string[];
  onChange: (next: MapPostsFilters) => void;
};

export const MAP_FILTER_BAR_HEIGHT = 64;

export function MapFilterBar({ filters, cities, eventTypes, onChange }: Props) {
  const { t } = useTranslation();
  const stripesColor = useThemeColor({}, "stripes");

  const recencyOptions: MapFilterDropdownOption[] = RECENCY_PRESETS.map((p) => ({
    value: p,
    label: t(RECENCY_LABEL_KEY[p]),
  }));

  const cityOptions: MapFilterDropdownOption[] = [
    { value: "", label: t("MAP_ANY_CITY") },
    ...cities.map((c) => ({ value: c, label: c })),
  ];

  const eventTypeOptions: MapFilterDropdownOption[] = [
    { value: "", label: t("MAP_ANY_EVENT_TYPE") },
    ...eventTypes.map((et) => {
      const meta = (
        NOTIFICATION_TYPES as Record<string, { icon: string; title: string }>
      )[et];
      return {
        value: et,
        label: meta ? t(meta.title) : et,
        icon: meta?.icon,
      };
    }),
  ];

  return (
    <View style={[styles.container, { borderBottomColor: stripesColor }]}>
      <View style={styles.field}>
        <MapFilterDropdown
          value={filters.recency}
          onChange={(v) =>
            onChange({ ...filters, recency: v as RecencyPreset })
          }
          options={recencyOptions}
          placeholder={t("MAP_FILTER_RECENCY")}
        />
      </View>

      <View style={styles.field}>
        <MapFilterDropdown
          value={filters.city ?? ""}
          onChange={(v) => onChange({ ...filters, city: v || null })}
          options={cityOptions}
          placeholder={t("MAP_ANY_CITY")}
          disabled={cities.length === 0}
        />
      </View>

      <View style={styles.field}>
        <MapFilterDropdown
          value={filters.eventType ?? ""}
          onChange={(v) => onChange({ ...filters, eventType: v || null })}
          options={eventTypeOptions}
          placeholder={t("MAP_ANY_EVENT_TYPE")}
          disabled={eventTypes.length === 0}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  field: {
    flex: 1,
  },
});

export default MapFilterBar;
